import { Field, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Form, FormGroup, Row, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { ReduxState } from "../../../../../redux";
import { formikInput } from "../../../../../utils/utils";
import PopoverHelp from "../../../../Docuclipper/PopoverHelp";
import { LoadingTrackedButton } from "../../../../Docuclipper/TrackedButton";
import { downloadSpreadsheetHelper } from "./utils";
import { getQbdAccounts, QbdAccount } from "../../../../../docuclipper/api";

type Props = {};

const KB_BANK_IIF =
  "/docs/import-bank-statement-transactions-into-quickbooks-desktop-iif/";

export const IifOptions: React.FC<Props> = ({}) => {
  const [loading, setLoading] = React.useState(false);

  const dispatch = useDispatch();
  const { job } = useSelector((state: ReduxState) => state.JobData);

  const { downloadOptions, tls } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { selectedDocumentIds, selectedFieldNames } = downloadOptions;
  const tlsCategories = tls?.categories || [];

  const { selectedL1: l1, selectedL2: l2, tagLevels } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const projectTagLevelId: number | null =
    (l1 &&
      l2 &&
      tagLevels?.root?.[l1]?.[l2]?.metadata?.tagId &&
      Number(tagLevels.root[l1][l2].metadata.tagId)) ||
    null;

  const [coa, setCoa] = useState<QbdAccount[] | null>(null);
  useEffect(() => {
    if (!projectTagLevelId) {
      setCoa(null);
      return;
    }
    let cancelled = false;
    getQbdAccounts(projectTagLevelId)
      .then((res) => {
        if (cancelled) return;
        setCoa(res?.accounts || []);
      })
      .catch(() => {
        if (cancelled) return;
        setCoa([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectTagLevelId]);

  const hasCoa = coa !== null && coa.length > 0;
  const accountNames = hasCoa ? coa!.map((a) => a.name) : [];
  const nameSet = new Set(accountNames.map((n) => n.toLowerCase()));

  const projectSettingsHref =
    l1 && l2
      ? `/app/extractdata?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(
          l2
        )}&openProjectSettings=quickbooks`
      : "/app/extractdata";

  const onExport = ({
    accountId,
    bankId,
    accountType,
    fromAccount,
    useCategoryAsDestination,
  }) => {
    return downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "FieldsToExcel1",
      "iif",
      {
        accountId,
        bankId,
        accountType,
        fromAccount,
        useCategoryAsDestination,
        getTotals: false,
      },
      selectedDocumentIds,
      selectedFieldNames,
      [],
      [
        {
          field: "fieldName",
          operator: "equals",
          value: "bankMode",
        },
      ],
      "date",
      "asc"
    );
  };
  const onSubmit =
    (format: "iif" | "iif") =>
    (
      { accountId, bankId, accountType, fromAccount, useCategoryAsDestination },
      { resetForm }
    ) => {
      setLoading(true);
      onExport({
        accountId,
        bankId,
        fromAccount,
        accountType,
        useCategoryAsDestination,
      })
        .then(() => {
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });
    };

  return (
    <Formik
      initialValues={{
        accountId: "",
        bankId: "",
        accountType: "checking",
        fromAccount: "",
        useCategoryAsDestination: true,
      }}
      validationSchema={Yup.object().shape({
        accountId: Yup.string().required("Source account is required"),
        fromAccount: Yup.string().when("useCategoryAsDestination", {
          is: false,
          then: Yup.string().required("Destination account is required"),
          otherwise: Yup.string().notRequired(),
        }),
      })}
      onSubmit={onSubmit("iif")}
    >
      {({ submitForm, values, setFieldValue }) => {
        const sourceMismatch =
          hasCoa &&
          values.accountId.trim().length > 0 &&
          !nameSet.has(values.accountId.trim().toLowerCase());
        const destMismatch =
          hasCoa &&
          !values.useCategoryAsDestination &&
          values.fromAccount.trim().length > 0 &&
          !nameSet.has(values.fromAccount.trim().toLowerCase());

        const unmatchedCategories =
          hasCoa && values.useCategoryAsDestination
            ? (tlsCategories || [])
                .filter(c => c && c.trim().length > 0)
                .filter(c => !nameSet.has(c.trim().toLowerCase()))
            : [];
        return (
          <>
            {hasCoa ? (
              <Alert variant="info" className="small mb-3">
                <FontAwesomeIcon icon="check-circle" className="mr-2" />
                Validating against {coa!.length} accounts in your QuickBooks
                Desktop Chart of Accounts.{" "}
                <a
                  href={projectSettingsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Manage Chart of Accounts
                </a>
              </Alert>
            ) : (
              <Alert variant="warning" className="small mb-3">
                <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                <strong>
                  Account names in your IIF must match QuickBooks Desktop
                  exactly.
                </strong>{" "}
                <a
                  href={projectSettingsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Upload your Chart of Accounts
                </a>{" "}
                so we can validate before download.{" "}
                <a href={KB_BANK_IIF} target="_blank" rel="noopener noreferrer">
                  Learn more
                </a>
              </Alert>
            )}

            {hasCoa && (
              <datalist id="qbd-coa-accounts-list">
                {accountNames.map((n, i) => (
                  <option key={`${n}-${i}`} value={n} />
                ))}
              </datalist>
            )}

            <FormGroup>
              <Form.Label>
                <strong>Source Account</strong>
              </Form.Label>
              <PopoverHelp header="" id="iifFromAccountHelp" placement="bottom">
                The name of the account assigned to the transaction. Must match
                exactly with an account name in QuickBooks.
              </PopoverHelp>
              <Field
                name="accountId"
                type="text"
                component={formikInput}
                list={hasCoa ? "qbd-coa-accounts-list" : undefined}
                autoComplete="off"
              ></Field>
              {sourceMismatch && (
                <Form.Text className="text-warning">
                  <FontAwesomeIcon icon="exclamation-triangle" className="mr-1" />
                  "{values.accountId}" is not in your QuickBooks Desktop Chart
                  of Accounts. Double-check the spelling or upload an updated
                  COA.
                </Form.Text>
              )}
            </FormGroup>

            <FormGroup>
              <Form.Label>
                <strong>Destination Account Type</strong>
              </Form.Label>
              <div>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="radio"
                    id="useCategoryAsDestination"
                    name="useCategoryAsDestination"
                    label="Map to transaction category"
                    checked={values.useCategoryAsDestination}
                    onChange={() =>
                      setFieldValue("useCategoryAsDestination", true)
                    }
                  />
                  <PopoverHelp header="" id="iifCategoryHelp" placement="bottom">
                    When using categories, the category names must match exactly
                    with account names in QuickBooks.
                  </PopoverHelp>
                </div>
                <Form.Check
                  type="radio"
                  id="useCustomDestination"
                  name="useCategoryAsDestination"
                  label="Use a fixed account name"
                  checked={!values.useCategoryAsDestination}
                  onChange={() =>
                    setFieldValue("useCategoryAsDestination", false)
                  }
                />
              </div>
            </FormGroup>

            {!values.useCategoryAsDestination && (
              <FormGroup>
                <Form.Label>
                  <strong>Destination Account</strong>
                </Form.Label>
                <PopoverHelp header="" id="iifToAccountHelp" placement="bottom">
                  The income or expense account to which you assigned the
                  transaction. Must match exactly with an account name in
                  QuickBooks.
                </PopoverHelp>
                <Field
                  name="fromAccount"
                  type="text"
                  component={formikInput}
                  list={hasCoa ? "qbd-coa-accounts-list" : undefined}
                  autoComplete="off"
                ></Field>
                {destMismatch && (
                  <Form.Text className="text-warning">
                    <FontAwesomeIcon
                      icon="exclamation-triangle"
                      className="mr-1"
                    />
                    "{values.fromAccount}" is not in your QuickBooks Desktop
                    Chart of Accounts. Double-check the spelling or upload an
                    updated COA.
                  </Form.Text>
                )}
              </FormGroup>
            )}

            {unmatchedCategories.length > 0 && (
              <Alert variant="warning" className="small mb-3">
                <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                <strong>
                  {unmatchedCategories.length} categor
                  {unmatchedCategories.length === 1 ? "y is" : "ies are"} not in
                  your QuickBooks Desktop Chart of Accounts:
                </strong>
                <ul className="mb-1 mt-1 pl-4">
                  {unmatchedCategories.slice(0, 10).map(c => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                {unmatchedCategories.length > 10 && (
                  <div className="mb-1">
                    ...and {unmatchedCategories.length - 10} more.
                  </div>
                )}
                Edit categories in the transaction grid to match QuickBooks
                Desktop account names, or{" "}
                <a
                  href={projectSettingsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  add the missing names to your Chart of Accounts
                </a>
                . QuickBooks will reject any line whose category does not exist.
              </Alert>
            )}

            <Row className="justify-content-end mx-1">
              <LoadingTrackedButton
                isLoading={loading}
                className="btn-save-iif justify-content-center m-2"
                variant="primary"
                onClick={() => {
                  submitForm();
                }}
              >
                Download IIF
              </LoadingTrackedButton>
            </Row>
          </>
        );
      }}
    </Formik>
  );
};
export default IifOptions;
